import { Space } from 'antd'
import CIMSCollapse from 'components/CIMSCollapse'
import { CoreFormItemsProps, CoreFormItemsValue } from './types'
import { useEffect, useState } from 'react'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { Moment } from 'moment'
import { useCollapseButton } from '../../hooks/useCollapseButton'
import TabularFormItems from 'components/TabularFormItems'
import { useBreakDataFormItemColumns } from '../../hooks/useBreakDataFormItemColumns'
import StrengthCount from '../StrengthCount'
import { alphabeticalCompare } from '@cims-online/shared'
import CIMSSection from 'components/CIMSSection'
import moment from 'moment'

const CoreFormItems: React.FC<CoreFormItemsProps> = ({
  onChange,
  value,
  concretePlacements,
}) => {
  const cores = concretePlacements.flatMap(
    ({ placementId, concreteCoreMeasurements }) =>
      concreteCoreMeasurements.map(
        ({ globalId, measurementId, concreteCoreBreakData }) => ({
          globalId,
          placementId,
          measurementId,
          concreteCoreBreakData,
        })
      )
  )
  const [values, setValues] = useState(value ?? [])
  useEffect(() => {
    onChange?.(values)
    // don't listen to onChange so we don't trigger infinite re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])
  function change<Key extends keyof CoreFormItemsValue>(
    key: Key,
    { concreteCoreMeasurementId: id }: CoreFormItemsValue,
    value: CoreFormItemsValue[Key]
  ) {
    setValues((prev) => {
      const found = prev.find(
        ({ concreteCoreMeasurementId }) => concreteCoreMeasurementId === id
      )
      const resolved: CoreFormItemsValue = {
        ...found,
        concreteCoreMeasurementId: id,
        [key]: value,
      }
      if (
        key === 'age' &&
        !!resolved.placementDate &&
        typeof value === 'number'
      )
        resolved.breakDate = moment(resolved.placementDate).add(value, 'days')
      else if (
        key === 'breakDate' &&
        !!resolved.breakDate &&
        !!resolved.placementDate
      )
        resolved.age = resolved.breakDate.diff(
          moment(resolved.placementDate),
          'days'
        )
      return [
        ...prev.filter(
          ({ concreteCoreMeasurementId }) => concreteCoreMeasurementId !== id
        ),
        resolved,
      ]
    })
  }
  const { CollapseButton, ...rest } = useCollapseButton(cores)
  const columns = useBreakDataFormItemColumns<CoreFormItemsValue>({
    type: 'core',
    onChange: {
      age: (r, v) => change('age', r, v as number),
      breakDate: (r, v) => change('breakDate', r, v as Moment),
      diameter: (r, v) => change('diameter', r, v as string),
      labNumber: (r, v) => change('labNumber', r, v as string),
      labTech: (r, v) => change('labTech', r, v as LabeledValueType),
      peakLoad: (r, v) => change('peakLoad', r, v as number),
      strength: (r, v) => change('strength', r, v as number),
      testingLab: (r, v) => change('testingLab', r, v as LabeledValueType),
      type: (r, v) => change('type', r, v as string),
    },
  })
  const header = ({
    placementId,
    measurementId,
    globalId,
  }: (typeof cores)[0]) =>
    placementId && measurementId
      ? `${placementId}-${measurementId}`
      : measurementId ?? placementId ?? globalId
  return (
    <Space size="small" direction="vertical" style={{ width: '100%' }}>
      <CIMSSection title="Cores">
        <CIMSCollapse
          {...rest}
          multiple
          overflowX
          panels={cores
            .sort((a, b) => {
              const A = header(a)
              const B = header(b)
              return alphabeticalCompare(A, B)
            })
            .map((core) => {
              const { globalId } = core
              return {
                key: globalId,
                header: header(core),
                extra: {
                  type: 'default',
                  children: (
                    <StrengthCount
                      count={
                        values.find(
                          ({ concreteCoreMeasurementId }) =>
                            globalId === concreteCoreMeasurementId
                        )?.strength
                          ? 1
                          : 0
                      }
                      max={1}
                    />
                  ),
                },
                children: (
                  <TabularFormItems
                    records={values.find(
                      ({ concreteCoreMeasurementId }) =>
                        globalId === concreteCoreMeasurementId
                    )}
                    rowKey="concreteCoreMeasurementId"
                    columns={columns}
                  />
                ),
              }
            })}
        />
        {CollapseButton}
      </CIMSSection>
    </Space>
  )
}

export default CoreFormItems
